import { Bot, FileText } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import type { OptimizationQualityEval } from '../../lib/optimization/qualityEval'
import {
  collectQualityIssues,
  type QualityIssue,
} from '../../lib/optimization/qualityEvalFlagging'
import type { OptimizationAgentUiState } from '../../lib/optimization/optimizationAgentUi'
import {
  type ResumeColorPreference,
  resolveResumeColors,
} from '../../types/resumeColorScheme'
import {
  RESUME_TEMPLATES,
  type ResumeTemplateId,
} from '../../types/resumeTemplate'
import { qualityGateStatusLabel } from './optimization/OptimizationQualityGate'
import ResumeAccentColorPicker from './ResumeAccentColorPicker'

type OpenPanel = 'template' | 'color' | null

export interface OptimizationAgentRailSlot {
  headline: string
  headerStatus: OptimizationAgentUiState['headerStatus']
  onExpand: () => void
  /** Collapse the side panel back to the rail icon (toggle when `panelOpen`). */
  onCollapse?: () => void
  /** Side panel is expanded — highlight rail icon; click collapses when `onCollapse` is set. */
  panelOpen?: boolean
  qualityEval?: OptimizationQualityEval | null
  qualityIssues?: QualityIssue[]
}

interface ResumePreviewSettingsRailProps {
  template: ResumeTemplateId
  onTemplateChange: (templateId: ResumeTemplateId) => void
  colorPreference: ResumeColorPreference
  onColorPreferenceChange: (preference: ResumeColorPreference) => void
  agentSlot?: OptimizationAgentRailSlot | null
}

function railIconButtonClass(active: boolean): string {
  return `relative flex h-10 w-10 items-center justify-center rounded-lg border transition-colors ${
    active
      ? 'border-interactive bg-interactive-subtle text-interactive'
      : 'border-border-default text-text-secondary hover:bg-surface-muted hover:text-text-primary'
  }`
}

function agentAttentionBadge(status: OptimizationAgentUiState['headerStatus']): boolean {
  return status === 'error' || status === 'working'
}

export default function ResumePreviewSettingsRail({
  template,
  onTemplateChange,
  colorPreference,
  onColorPreferenceChange,
  agentSlot = null,
}: ResumePreviewSettingsRailProps) {
  const [openPanel, setOpenPanel] = useState<OpenPanel>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const templateLabelId = useId()
  const colorLabelId = useId()
  const resolvedColor = resolveResumeColors(colorPreference)
  const agentQualityIssues = useMemo(
    () =>
      agentSlot?.qualityEval
        ? agentSlot.qualityIssues ?? collectQualityIssues(agentSlot.qualityEval)
        : [],
    [agentSlot]
  )

  useEffect(() => {
    if (!openPanel) return undefined
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpenPanel(null)
      }
    }
    document.addEventListener('mousedown', onDocumentMouseDown)
    return () => document.removeEventListener('mousedown', onDocumentMouseDown)
  }, [openPanel])

  const togglePanel = (panel: Exclude<OpenPanel, null>) => {
    setOpenPanel((current) => (current === panel ? null : panel))
  }

  const qualityNeedsAttention =
    agentSlot?.qualityEval != null &&
    qualityGateStatusLabel(agentSlot.qualityEval, agentQualityIssues) !== 'Ready'

  return (
    <div
      ref={rootRef}
      className="resume-preview-settings-rail relative z-20 flex w-14 shrink-0 flex-col items-center gap-2 self-stretch border-l border-border-default bg-surface-default py-3"
      data-testid="resume-preview-settings-rail"
    >
      <button
        type="button"
        className={railIconButtonClass(openPanel === 'template')}
        title="Resume template"
        aria-label="Resume template"
        aria-expanded={openPanel === 'template'}
        aria-haspopup="dialog"
        onClick={() => togglePanel('template')}
      >
        <FileText className="h-5 w-5" aria-hidden />
      </button>

      <button
        type="button"
        className={railIconButtonClass(openPanel === 'color')}
        title="Resume accent color"
        aria-label="Resume accent color"
        aria-expanded={openPanel === 'color'}
        aria-haspopup="dialog"
        data-testid="resume-color-picker-trigger"
        onClick={() => togglePanel('color')}
      >
        <span
          className="h-4 w-4 rounded-full ring-1 ring-black/10"
          style={{ backgroundColor: resolvedColor.accentHex }}
          aria-hidden
        />
      </button>

      <div className="min-h-0 flex-1" aria-hidden />

      {agentSlot ? (
        <button
          type="button"
          data-testid="optimization-agent-rail-reopen"
          onClick={() => {
            if (agentSlot.panelOpen) {
              agentSlot.onCollapse?.()
            } else {
              agentSlot.onExpand()
            }
          }}
          className={railIconButtonClass(agentSlot.panelOpen ?? false)}
          aria-label={
            agentSlot.panelOpen
              ? `Collapse tailoring agent — ${agentSlot.headline || 'In progress'}`
              : `Open tailoring agent — ${agentSlot.headline || 'In progress'}`
          }
          aria-pressed={agentSlot.panelOpen ?? false}
          title={`Tailoring agent — ${agentSlot.headline || 'In progress'}`}
        >
          <Bot className="h-5 w-5 shrink-0 text-brand-primary" aria-hidden />
          {agentAttentionBadge(agentSlot.headerStatus) || qualityNeedsAttention ? (
            <span
              className="absolute right-1 top-1 h-2 w-2 rounded-full border-2 border-surface-default bg-warning"
              aria-hidden
              data-testid="optimization-agent-rail-badge"
            />
          ) : null}
        </button>
      ) : null}

      {openPanel === 'template' && (
        <div
          role="dialog"
          aria-labelledby={templateLabelId}
          className="absolute right-full top-0 mr-3 w-52 rounded-xl border border-border-default bg-surface-default p-3 shadow-lg"
          data-testid="resume-template-panel"
        >
          <div
            id={templateLabelId}
            className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted"
          >
            Template
          </div>
          <div className="space-y-2">
            {RESUME_TEMPLATES.map((item) => {
              const active = template === item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    onTemplateChange(item.id)
                    setOpenPanel(null)
                  }}
                  className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                    active
                      ? 'border-interactive bg-interactive-subtle text-text-primary'
                      : 'border-border-default text-text-secondary hover:bg-surface-muted hover:text-text-primary'
                  }`}
                >
                  <div className="text-sm font-medium">{item.label}</div>
                  <div className="mt-1 text-xs leading-5 text-text-muted">
                    {item.description}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {openPanel === 'color' && (
        <div
          role="dialog"
          aria-labelledby={colorLabelId}
          className="absolute right-full top-12 mr-3 w-[min(21rem,calc(100vw-2rem))] rounded-xl border border-border-default bg-surface-default p-4 shadow-xl"
          data-testid="resume-color-picker-panel"
        >
          <div
            id={colorLabelId}
            className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-muted"
          >
            Color
          </div>
          <ResumeAccentColorPicker
            preference={colorPreference}
            onChange={onColorPreferenceChange}
          />
        </div>
      )}
    </div>
  )
}
